/**
 * 他の端末で起きた状態変化の種類。取得結果の差分から読み取る。
 * 状態のイベントは「誰が」を保存しない（非永続の設計）ため、自端末の操作かどうかは画面側で判定する。
 */
export const REMOTE_CHANGE_KINDS = [
    'clerk-confirm',
    'clerk-change',
    'clerk-stop',
    'clerk-undo',
    'pharm-start',
    'pharm-undo',
    'stop-ack',
    'admin-start',
    'admin-undo',
    'pharm-done',
    'pharm-done-undo',
    'admin-done',
    'admin-done-undo',
];
/**
 * 直前の取得結果と今回の取得結果を比べ、状態が変わった案件を1件につき1つの変化として返す。
 *
 * ・両方に存在する案件だけを見る（名簿の追加・削除や日付の切り替えは通知しない）
 * ・取り消しは下流もまとめて消える（クラークの取消で調製開始・投与開始も消える）ため、
 *   一番上流の取り消しとして1件にまとめる
 * ・同じ間隔の中で複数の入力があった場合は、いちばん新しい入力（seq が最大）を採る
 */
export function detectChanges(prev, next) {
    const byId = new Map(prev.map((c) => [c.caseId, c]));
    const out = [];
    for (const after of next) {
        const before = byId.get(after.caseId);
        if (!before)
            continue;
        const kind = changeKind(before, after);
        if (kind)
            out.push({ kind, before, after });
    }
    return out;
}
function changeKind(before, after) {
    if (before.clerk && !after.clerk)
        return 'clerk-undo';
    if (before.pharm && !after.pharm)
        return 'pharm-undo';
    if (before.admin && !after.admin)
        return 'admin-undo';
    // 開始の取り消しは完了も一緒に消すため、完了だけが消えたときに限って完了の取り消しとする
    if (before.pharmDone && !after.pharmDone)
        return 'pharm-done-undo';
    if (before.adminDone && !after.adminDone)
        return 'admin-done-undo';
    const added = [];
    if (after.clerk && after.clerk.seq !== before.clerk?.seq) {
        added.push({ kind: `clerk-${after.clerk.type}`, seq: after.clerk.seq });
    }
    if (after.pharm && after.pharm.seq !== before.pharm?.seq) {
        added.push({ kind: 'pharm-start', seq: after.pharm.seq });
    }
    if (after.stopAck && after.stopAck.seq !== before.stopAck?.seq) {
        added.push({ kind: 'stop-ack', seq: after.stopAck.seq });
    }
    if (after.admin && after.admin.seq !== before.admin?.seq) {
        added.push({ kind: 'admin-start', seq: after.admin.seq });
    }
    if (after.pharmDone && after.pharmDone.seq !== before.pharmDone?.seq) {
        added.push({ kind: 'pharm-done', seq: after.pharmDone.seq });
    }
    if (after.adminDone && after.adminDone.seq !== before.adminDone?.seq) {
        added.push({ kind: 'admin-done', seq: after.adminDone.seq });
    }
    if (!added.length)
        return null;
    return added.reduce((a, b) => (b.seq > a.seq ? b : a)).kind;
}
/**
 * 職種ごとに知らせる変化。
 * 依頼（2026-09-23）により、どの職種にも他端末のすべての状態変化を知らせる。
 * 絞り込みが要るようになったら、ここで職種ごとに外す（画面側の変更は要らない）。
 */
export const NOTICE_KINDS_BY_ROLE = {
    clerk: new Set(REMOTE_CHANGE_KINDS),
    pharm: new Set(REMOTE_CHANGE_KINDS),
    nurse: new Set(REMOTE_CHANGE_KINDS),
    admin: new Set(REMOTE_CHANGE_KINDS),
};
export const isNoticeFor = (role, kind) => NOTICE_KINDS_BY_ROLE[role].has(kind);
//# sourceMappingURL=changes.js.map